import { z } from 'zod'
import {
  ecsign,
  hashPersonalMessage,
  toBuffer,
  addHexPrefix,
  bufferToInt,
  fromSigned,
  intToHex,
  stripHexPrefix,
  toUnsigned,
} from '@ethereumjs/util'
import { padLeft } from 'web3-utils'
import { ValueObject } from '@lukso/controllerapps-sharedlogic-ddd-core'
import { HexNumber } from '@lukso/controllerapps-sharedlogic-primitives'

import { PrivateKey } from '../wallet/PrivateKey'
import { MessageSigningRequest } from './MessageSigningRequest'

const SignedMessageCreatePropsSchema = z.object({
  message: z.instanceof(MessageSigningRequest),
  privateKey: z.instanceof(PrivateKey),
})

type SignedMessageCreateProps = z.infer<typeof SignedMessageCreatePropsSchema>

type SignedMessageProps = {
  signedMessageValue: HexNumber
}

/**
 * Concatenate an extended ECDSA signature into a single '0x'-prefixed hex string.
 *
 * @param v - The 'v' portion of the signature.
 * @param r - The 'r' portion of the signature.
 * @param s - The 's' portion of the signature.
 * @returns The concatenated ECDSA signature as a '0x'-prefixed string.
 */
export const concatSig = (v: Buffer, r: Buffer, s: Buffer): string => {
  const rSig = fromSigned(r)
  const sSig = fromSigned(s)
  const vSig = bufferToInt(v)
  const rString = padLeft(toUnsigned(rSig).toString('hex'), 64)
  const sString = padLeft(toUnsigned(sSig).toString('hex'), 64)
  const vString = stripHexPrefix(intToHex(vSig))
  return addHexPrefix(`${rString}${sString}${vString}`)
}

export class SignedMessage extends ValueObject<SignedMessageProps> {
  public getValue(): HexNumber {
    return this.props.signedMessageValue
  }

  public static create(props: SignedMessageCreateProps): SignedMessage {
    const validatedProps = SignedMessageCreatePropsSchema.parse(props)

    const messageBuffer = validatedProps.message.toBuffer()
    const messageHash = hashPersonalMessage(messageBuffer)
    const privateKeyBuffer = validatedProps.privateKey.getValue()
    const sig = ecsign(messageHash, privateKeyBuffer)
    const hexNumberValue = concatSig(toBuffer(sig.v), sig.r, sig.s)
    const signedMessageValue = HexNumber.create({ hexNumberValue })

    return new SignedMessage({ signedMessageValue })
  }
}
